/**
 * THE THREAD HEADER'S ⋯ MENU (ITP-E-203-03, Plan Q9): carbon's order (OCA-E-160 `ChatPanel.tsx` 2985-3080) with ITP's
 * real verbs. Carbon's Clear is left out: no backend hides a whole thread and keeps its row.
 *
 * On the Messages page (ITP-E-204-09, Q13) Search is an icon in the header and the members are Details', so the menu is
 * Select, Starred, Rename, Leave. Delete stays, as in carbon's thread menu (`ChatPanel.tsx` 3075): it is how a reader
 * removes a group they have left from the list (PRD story 5), and the only conversation-level removal the page has.
 *
 * In the Chat panel the menu starts with Open in Messages and a divider (ITP-E-268-01, Plan ITP-P-58), for a reader who
 * holds OM-Messages: never on the page itself, nor in a context chat, which the page does not list.
 */

export type ThreadMenuItem = 'open' | 'search' | 'select' | 'starred' | 'members' | 'rename' | 'leave' | 'divider' | 'delete';

export interface ThreadMenuContext {
  isGroup: boolean;
  /** The reader has left this conversation. */
  hasLeft: boolean;
  /** The reader's permission in it (`ChatPermissions`): 290 is a group's admin. */
  maxPermission: number;
  /** The company's default chat, which nobody renames. */
  isDefault: boolean;
  /** A conversation about something (a case's chat): today it offers neither Leave nor Delete. */
  isContextChat?: boolean;
  /** Search in the thread (B-16) and the starred messages (B-12), while their flags are on. */
  flags: { search: boolean, starred: boolean };
  /** The Messages page's header, which has its own Search icon and leaves the members to Details. */
  onPage?: boolean;
  /** The reader holds OM-Messages and the conversation has an id (ITP-E-268-01). */
  canOpenInMessages?: boolean;
}

/**
 * The menu's items, in order: Open in Messages and a divider (ITP-E-268-01), Search, Select, Starred, Members, Rename,
 * Leave, a divider, then Delete (for me); on the Messages page, without Open in Messages, Search and Members.
 */
export function threadMenuItems(ctx: ThreadMenuContext): ThreadMenuItem[] {
  const inGroup = ctx.isGroup && !ctx.hasLeft;
  const items: ThreadMenuItem[] = [];
  if (ctx.canOpenInMessages && !ctx.onPage && !ctx.isContextChat) {
    items.push('open', 'divider');
  }
  if (ctx.flags.search && !ctx.onPage) {
    items.push('search');
  }
  items.push('select');
  if (ctx.flags.starred) {
    items.push('starred');
  }
  if (inGroup && !ctx.onPage) {
    items.push('members');
  }
  if (inGroup && ctx.maxPermission === 290 && !ctx.isDefault) {
    items.push('rename');
  }
  if (inGroup && !ctx.isContextChat) {
    items.push('leave');
  }
  if (!ctx.isContextChat) {
    items.push('divider', 'delete');
  }
  return items;
}

/**
 * A group's second header line on the Messages page, as carbon's (/meet/messages `page.tsx:315`): "4 members · 2 online";
 * the count alone while presence has not answered.
 */
export function membersLine(members: number, online: number | null): string {
  const count = `${members} ${members === 1 ? 'member' : 'members'}`;
  return online === null ? count : `${count} · ${online} online`;
}

/** The Select band's floor: "1 message selected", "3 messages selected". */
export function selectionLabel(count: number): string {
  return `${count} ${count === 1 ? 'message' : 'messages'} selected`;
}
